/**
 * The authentication calls of the foundation. They store a session and nothing else: no screens, no
 * navigation. Each takes the API client as a parameter so tests can pass a stub.
 */
import { publicApi } from '@/api/instances';
import { API_ERROR_CODES, ApiError } from '@/api/errors';
import type { ApiClient } from '@/api/client';
import type { JoinSessionResult, StaffLoginResult, TableContext } from '@/api/types';
import { guestSessionFromJoin, guestSessionStore } from './guest-session';
import type { GuestSession } from './guest-session';
import { staffSessionFromLogin, staffSessionStore } from './staff-session';
import type { StaffSession } from './staff-session';

/** `POST /api/v1/auth/staff/login`. A wrong password is a 401 that never clears an existing session. */
export async function loginStaff(
  credentials: { email: string; password: string },
  api: ApiClient = publicApi,
): Promise<StaffSession> {
  const result = await api.post<StaffLoginResult>('/auth/staff/login', {
    email: credentials.email.trim(),
    password: credentials.password,
  });
  const session = staffSessionFromLogin(result);
  staffSessionStore.set(session);
  return session;
}

/** `POST /api/v1/guest/tables/resolve`: what a scanned QR token points at. */
export function resolveTable(qrToken: string, api: ApiClient = publicApi): Promise<TableContext> {
  return api.post<TableContext>('/guest/tables/resolve', { qr_token: qrToken });
}

/** `POST /api/v1/guest/sessions/join`: starts or joins a dining session and stores its temporary token. */
export async function joinGuestSession(
  input: { qrToken: string; displayName: string; partySize: number },
  api: ApiClient = publicApi,
): Promise<GuestSession> {
  const result = await api.post<JoinSessionResult>('/guest/sessions/join', {
    qr_token: input.qrToken,
    display_name: input.displayName.trim(),
    party_size: input.partySize,
  });
  const session = guestSessionFromJoin({ ...input, displayName: input.displayName.trim(), result });
  if (!session) {
    throw new ApiError({
      status: 200,
      code: API_ERROR_CODES.INVALID_RESPONSE,
      message: 'The server sent a session this app cannot use.',
    });
  }
  guestSessionStore.set(session);
  return session;
}
